import React, { useEffect, useRef, useState, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { modalStack, MODAL_TIMINGS } from './modalStack';

export interface KlFullModalProps {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    footer?: React.ReactNode;
    origin?: { x: number; y: number } | null;
    children: React.ReactNode;
    id?: string;
    headerActions?: React.ReactNode;
    contentClassName?: string;
}

export const KlFullModal: React.FC<KlFullModalProps> = ({
    open,
    onClose,
    title,
    subtitle,
    footer,
    origin,
    children,
    id: propId,
    headerActions,
    contentClassName = ''
}) => {
    const autoId = useId();
    const modalId = propId || `kl-fullmodal-${autoId}`;

    const [isMounted, setIsMounted] = useState(false);
    const [animationState, setAnimationState] = useState<'closed' | 'opening' | 'open' | 'closing'>('closed');
    const [stackDepth, setStackDepth] = useState(0);
    const [computedHeightPx, setComputedHeightPx] = useState<number | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const prevActiveElementRef = useRef<HTMLElement | null>(null);
    const didUnmountRef = useRef(false);
    const lastViewportHeightRef = useRef<number>(0);

    const calculateModalHeight = useCallback(() => {
        if (typeof window === 'undefined') return;
        const totalHeight = window.innerHeight;
        const targetH = window.visualViewport ? window.visualViewport.height : totalHeight;
        if (targetH > 0) {
            setComputedHeightPx(prev => {
                if (prev !== null && Math.abs(prev - targetH) < 2) return prev;
                return targetH;
            });
        }
    }, []);

    const prefersReducedMotion = typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

    const cleanupAndUnmount = useCallback(() => {
        if (didUnmountRef.current) return;
        didUnmountRef.current = true;
        setIsMounted(false);
        setAnimationState('closed');
    }, []);

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Registro en la pila modal y gestión de montado
    useEffect(() => {
        if (open) {
            // Si ya está montado y abierto, no reiniciar animación ni re-registrar
            if (isMounted && animationState === 'open') {
                return;
            }

            didUnmountRef.current = false;
            prevActiveElementRef.current = document.activeElement as HTMLElement | null;
            setIsMounted(true);

            const unregister = modalStack.push({
                id: modalId,
                type: 'full',
                onClose: () => onCloseRef.current(),
                returnFocusElement: prevActiveElementRef.current
            });

            setStackDepth(modalStack.getDepth(modalId));

            // Animación de entrada
            setAnimationState('opening');
            const timer = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setAnimationState('open');
                });
            });

            return () => {
                cancelAnimationFrame(timer);
                unregister();
            };
        } else if (isMounted) {
            // Animación de salida
            setAnimationState('closing');

            // Respaldo de desmontaje seguro si transitionend no llega en duración + 50ms
            const durationMs = prefersReducedMotion
                ? MODAL_TIMINGS.reducedMotion.fadeMs
                : MODAL_TIMINGS.full.closeMs;

            const safetyTimer = setTimeout(() => {
                cleanupAndUnmount();
            }, durationMs + MODAL_TIMINGS.safetyMarginMs);

            return () => clearTimeout(safetyTimer);
        }
    }, [open, modalId, prefersReducedMotion, cleanupAndUnmount]);

    // Actualizar profundidad si la pila cambia
    useEffect(() => {
        if (!isMounted) return;
        return modalStack.subscribe(() => {
            setStackDepth(modalStack.getDepth(modalId));
        });
    }, [isMounted, modalId]);

    // Control de altura estable (Punto 1b: sin oscilaciones al desbloquear o volver de segundo plano)
    useEffect(() => {
        if (!open || !isMounted) return;

        calculateModalHeight();

        let resizeTimer: any = null;
        const handleViewportResize = () => {
            if (!window.visualViewport) return;
            const currentH = window.visualViewport.height;
            if (Math.abs(currentH - lastViewportHeightRef.current) < 2) return;

            if (resizeTimer) clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                requestAnimationFrame(() => {
                    const newH = window.visualViewport ? window.visualViewport.height : window.innerHeight;
                    if (Math.abs(newH - lastViewportHeightRef.current) >= 2) {
                        lastViewportHeightRef.current = newH;
                        calculateModalHeight();
                    }
                });
            }, 100);
        };

        if (window.visualViewport) {
            lastViewportHeightRef.current = window.visualViewport.height;
            window.visualViewport.addEventListener('resize', handleViewportResize);
        }

        let appStateHandle: any = null;
        if (Capacitor.isPluginAvailable('App')) {
            CapacitorApp.addListener('appStateChange', (state) => {
                if (state.isActive) {
                    setTimeout(() => {
                        requestAnimationFrame(() => {
                            calculateModalHeight();
                        });
                    }, 50);
                }
            }).then(h => { appStateHandle = h; }).catch(() => {});
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setTimeout(() => {
                    requestAnimationFrame(() => {
                        calculateModalHeight();
                    });
                }, 50);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleViewportResize);
            }
            if (resizeTimer) clearTimeout(resizeTimer);
            if (appStateHandle?.remove) appStateHandle.remove();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [open, isMounted, calculateModalHeight]);

    // Foco atrapado y tecla Escape
    useEffect(() => {
        if (animationState !== 'open') return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && modalStack.isTop(modalId)) {
                e.preventDefault();
                onClose();
                return;
            }

            if (e.key === 'Tab' && modalRef.current) {
                const focusables = modalRef.current.querySelectorAll<HTMLElement>(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];

                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [animationState, modalId, onClose]);

    if (!isMounted) return null;

    // Transform Origin según punto de origen del botón que lo abrió
    const transformOrigin = origin ? `${origin.x}px ${origin.y}px` : 'center center';
    const zIndex = 110 + stackDepth * 10;

    const containerStyle: React.CSSProperties = prefersReducedMotion
        ? {
            transformOrigin,
            zIndex,
            transform: 'none',
            bottom: 0,
            height: computedHeightPx !== null && computedHeightPx > 0 ? `${computedHeightPx}px` : '100%',
            opacity: animationState === 'opening' || animationState === 'closing' ? 0 : 1,
            transition: `opacity ${MODAL_TIMINGS.reducedMotion.fadeVar} ease`,
            willChange: animationState === 'opening' || animationState === 'closing' ? 'opacity' : 'auto'
        }
        : {
            transformOrigin,
            zIndex,
            transform: animationState === 'opening' || animationState === 'closing' ? 'scale(0.94)' : 'scale(1)',
            bottom: 0,
            height: computedHeightPx !== null && computedHeightPx > 0 ? `${computedHeightPx}px` : '100%',
            opacity: animationState === 'opening' || animationState === 'closing' ? 0 : 1,
            transition: animationState === 'closing'
                ? `transform ${MODAL_TIMINGS.full.closeVar} ${MODAL_TIMINGS.easeIn}, opacity ${MODAL_TIMINGS.full.closeVar} ${MODAL_TIMINGS.easeIn}`
                : `transform ${MODAL_TIMINGS.full.openVar} ${MODAL_TIMINGS.easeOut}, opacity ${MODAL_TIMINGS.full.openVar} ${MODAL_TIMINGS.easeOut}`,
            willChange: animationState === 'opening' || animationState === 'closing' ? 'transform, opacity' : 'auto'
        };

    return createPortal(
        <div
            id={modalId}
            ref={modalRef}
            data-kl-fullmodal="true"
            data-kl-modal-open={animationState === 'open' ? 'true' : 'false'}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${modalId}-title`}
            className="kl-fullmodal-container fixed inset-0 bg-[#F1F5F9] dark:bg-[#0F172A] flex flex-col overflow-hidden"
            style={containerStyle}
            onTransitionEnd={(e) => {
                if (e.target === modalRef.current && animationState === 'closing') {
                    cleanupAndUnmount();
                }
            }}
        >
            {/* Barra Azul Superior de 52px con Safe Area Top */}
            <div
                className="bg-[#2563EB] text-white flex items-center justify-between px-3 shrink-0 shadow-sm z-10"
                style={{
                    height: 'calc(52px + var(--kl-safe-top, env(safe-area-inset-top, 0px)))',
                    paddingTop: 'var(--kl-safe-top, env(safe-area-inset-top, 0px))'
                }}
            >
                <div className="flex-1 min-w-0 pr-2">
                    <h2
                        id={`${modalId}-title`}
                        className="text-[16px] font-bold text-white truncate m-0 leading-tight"
                    >
                        {title}
                    </h2>
                    {subtitle && (
                        <div className="text-[11px] text-blue-100 truncate mt-0.5">
                            {subtitle}
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-1 shrink-0">
                    {headerActions}

                    {/* Botón Cerrar X con área táctil 40x40 */}
                    <button
                        type="button"
                        data-drawer-close="true"
                        data-kl-modal-close="true"
                        onClick={onClose}
                        aria-label="Cerrar pantalla completa"
                        className="w-10 h-10 rounded-full hover:bg-white/15 active:bg-white/25 flex items-center justify-center transition-colors text-white"
                    >
                        <span className="material-symbols-outlined text-[22px]">close</span>
                    </button>
                </div>
            </div>

            {/* Cuerpo con scroll propio (.sc / .pad: padding 10px 12px, display flex flex-col gap 10px) */}
            <div
                className={`flex-1 overflow-y-auto overscroll-contain px-3 py-2.5 flex flex-col gap-2.5 ${contentClassName}`}
                style={{ 
                    WebkitOverflowScrolling: 'touch',
                    paddingBottom: footer ? undefined : 'calc(14px + var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)))'
                }}
            >
                {children}
            </div>

            {/* Pie fijo (.foot: padding 10px 12px 14px + safe area bottom) */}
            {footer && (
                <div
                    className="px-3 pt-2.5 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-[#1a2634] shrink-0"
                    style={{
                        paddingBottom: 'calc(14px + var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)))'
                    }}
                >
                    {footer}
                </div>
            )}
        </div>,
        document.body
    );
};

export default KlFullModal;
